import { FC, useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomPopup from "../../../components/CustomPopup/CustomPopup";
import CallUsPopUp from "../CallUsPopUp/CallUsPopUp";
import EmailUsPopUp from "../EmailUsPopUp/EmailUsPopUp";
import CallUsIcon from "../../../assets/icons/PhoneIcon.svg";
import ChatWithUsIcon from "../../../assets/icons/ChatWithUsIcon.svg";
import EmailIcon from "../../../assets/icons/EmailIcon.svg";
import FAQsMobile from "../FAQsMobile/FAQsMobile";
import FAQIcon from "../../../assets/icons/HelpFAQIcon.svg";
import CsSessionStorageUtils from "../../../utils/CsSessionStorageUtils";
import CustomChatWithUs from "../../../components/CustomChatWithUs/CustomChatWithUs";
import { triggerCleverTapEvent } from "../../../utils/CleverTap";
import { SCREEN_TYPE } from "../../../utils/constants";
import { useAppTranslation } from "../../../hooks/useAppTranslation";

interface Props {
  open: boolean;
  handleOpen: Function;
  functionalityCode?: string;
  LobId?: string;
  showChatWithUsBtn?: boolean;
}

const CallUs: FC<Props> = ({
  open,
  handleOpen,
  functionalityCode = "",
  LobId = "",
  showChatWithUsBtn = false,
}) => {
  const [isCallUsOpen, setIsCallUsOpen] = useState(false);
  const [isEmailUsOpen, setIsEmailUsOpen] = useState(false);
  const [showChatWithUs, setShowChatWithUs] = useState(false);
  const [isFaqOpen, setIsFaqOpen] = useState<boolean>(false);
  const { t } = useAppTranslation("common");

  const handleClosePopup = useCallback(() => {
    handleOpen(false);
  }, [handleOpen]);

  const handleCloseChat = useCallback(() => {
    setShowChatWithUs(false);
  }, []);

  const handleCallUsClick = useCallback(() => {
    triggerCleverTapEvent("get_help_additional_cta_click", {
      ScreenName: "Dashboard",
      isPrevPageNameRequired: true,
      SectionTitle: "we’re_just_a_tap_away_always",
      ctaText: "call_us",
    });
    setIsCallUsOpen(true);
  }, []);

  // useCallback for handleEmailUsClick
  const handleEmailUsClick = useCallback(() => {
    triggerCleverTapEvent("get_help_additional_cta_click", {
      ScreenName: "Dashboard",
      isPrevPageNameRequired: true,
      SectionTitle: "we’re_just_a_tap_away_always",
      ctaText: "email_us",
    });
    setIsEmailUsOpen(true);
  }, []);

  // useCallback for handleChatWithUsClick
  const handleChatWithUsClick = useCallback(() => {
    triggerCleverTapEvent("get_help_additional_cta_click", {
      ScreenName: "Dashboard",
      isPrevPageNameRequired: true,
      SectionTitle: "we’re_just_a_tap_away_always",
      ctaText: "chat_with_us",
    });
    setShowChatWithUs(true);
  }, []);

  const handleFaqClick = useCallback(() => {
    triggerCleverTapEvent("get_help_additional_cta_click", {
      ScreenName: "Dashboard",
      isPrevPageNameRequired: true,
      SectionTitle: "we’re_just_a_tap_away_always",
      ctaText: "faqs",
    });
    setIsFaqOpen(true);
  }, []);

  const renderSteps = () => {
    return (
      <Box className="need-help">
        <Typography
          fontSize="1.125rem"
          fontWeight={"600"}
          lineHeight={"1.35rem"}
          textAlign="center"
          color={"#333638"}
          marginTop={"1rem"}
        >
          {t("generalApp.titles.needAnyHelp")}
        </Typography>

        <Box
          display="flex"
          alignItems={"center"}
          justifyContent={"center"}
          marginTop={"1.4375rem"}
          marginBottom={"1.875rem"}
        >
          <Box
            textAlign="center"
            onClick={handleEmailUsClick}
            style={{ cursor: "pointer" }}
          >
            <img
              src={EmailIcon}
              alt="Email Us Icon"
              style={{
                width: "2.5rem",
                height: "2.5rem",
                marginBottom: ".9375rem",
              }}
            />
            <Typography sx={{ fontWeight: "600" }}>
              {t("generalApp.titles.emailUs")}
            </Typography>
          </Box>
          <Box
            textAlign={"center"}
            marginLeft={"2.375rem"}
            onClick={handleCallUsClick}
            style={{ cursor: "pointer" }}
          >
            <img
              src={CallUsIcon}
              alt="Call Us Icon"
              style={{
                width: "2.5rem",
                height: "2.5rem",
                marginBottom: ".9375rem",
              }}
            />
            <Typography sx={{ fontWeight: "600" }}>
              {t("generalApp.titles.callUs")}
            </Typography>
          </Box>
          <Box
            textAlign={"center"}
            marginLeft={"2.375rem"}
            onClick={handleFaqClick}
            style={{ cursor: "pointer" }}
          >
            <img
              src={FAQIcon}
              alt="Call Us Icon"
              style={{
                width: "2.5rem",
                height: "2.5rem",
                marginBottom: ".9375rem",
              }}
            />
            <Typography sx={{ fontWeight: "600" }}>
              {t("generalApp.titles.faqs")}
            </Typography>
          </Box>
        </Box>
        {showChatWithUsBtn && (
          <Box
            display="flex"
            alignItems={"center"}
            justifyContent={"center"}
            marginTop={"1.4375rem"}
            marginBottom={"1.875rem"}
          >
            <Box
              textAlign={"center"}
              onClick={handleChatWithUsClick}
              style={{ cursor: "pointer" }}
            >
              <img
                src={ChatWithUsIcon}
                alt="Call Us Icon"
                style={{
                  width: "1.5rem",
                  height: "1.5rem",
                  marginBottom: ".9375rem",
                  backgroundColor: "#f9f2f2",
                  borderRadius: "50%",
                  padding: "0.5rem",
                }}
              />
              <Typography sx={{ fontWeight: "600" }}>
                {t("generalApp.titles.chatWithUs")}
              </Typography>
            </Box>
          </Box>
        )}
        {isCallUsOpen && (
          <CallUsPopUp open={isCallUsOpen} handleOpen={setIsCallUsOpen} />
        )}
        {isEmailUsOpen && (
          <EmailUsPopUp open={isEmailUsOpen} handleOpen={setIsEmailUsOpen} />
        )}
        {showChatWithUs && (
          <CustomChatWithUs
            showIframe={showChatWithUs}
            onClose={handleCloseChat}
            functionalityCode={functionalityCode}
            LobId={LobId}
          />
        )}
        {isFaqOpen && (
          <FAQsMobile
            fromDetail={true}
            activeTab={CsSessionStorageUtils.get("ACTIVE_DASHBOARD_TAB")}
            open={isFaqOpen}
            handleOpen={setIsFaqOpen}
            openChatWithUs={handleChatWithUsClick}
          />
        )}
      </Box>
    );
  };

  return (
    <CustomPopup
      open={open}
      screenType={SCREEN_TYPE.HFScreenType}
      ScreenName="Housing Finance Servicing Detail Screen"
      handleOpen={handleClosePopup}
      btnLabel={t("generalApp.actionButtons.close")}
      className="add-callus-popup"
      btnStartIcon={<CloseIcon sx={{ fontSize: "1rem" }} />}
    >
      <Box className="add-callus-popup-content">{renderSteps()}</Box>
    </CustomPopup>
  );
};
export default CallUs;
